<script setup lang="ts">
import Rating from 'primevue/rating'

const props = defineProps<{
  initialStars: number
}>()
const emit = defineEmits<{
  (e: 'close', payload: { closeModal: boolean }): void
  (e: 'set-stars', arg1: number): void
}>()
const { t } = useLocale()
const isMobile = inject<Ref<boolean>>('isMobile')

const value = ref<number | undefined>(props.initialStars || undefined)
const previousRatingStored = computed(() => !!props.initialStars)

watch(value, curr => {
  if (!previousRatingStored.value && curr && Number.isInteger(curr)) {
    setTimeout(() => {
      emit('set-stars', curr)
    }, 250)
  }
})
</script>

<template>
  <ModalCenter
    :mobile-full="false"
    content-overflow
    :add-close-button="isMobile"
    no-actions-class
    @close="emit('close', { closeModal: true })"
  >
    <template #header>
      <div class="relative min-h-[5rem] w-full md:min-h-0">
        <CloseIcon class="hidden md:block" @click="emit('close', { closeModal: true })" />
        <img
          class="absolute -top-[3.5rem] left-1/2 block h-auto w-[7rem] -translate-x-1/2 md:hidden"
          src="/images/icons/smartcards/ready.svg"
          alt="mcd bag"
        />
      </div>
    </template>
    <template #content>
      <section
        data-test="rating-stars"
        class="flex w-full flex-col gap-4 p-4 md:mx-auto md:my-10 md:w-[60%] md:translate-y-0 md:p-0"
      >
        <figure class="flex flex-col items-center justify-center gap-8">
          <img
            class="hidden h-auto w-[7rem] md:block"
            src="/images/icons/smartcards/ready.svg"
            alt="mcd bag"
          />
          <h3 class="text-center text-lg font-bold tracking-[-0.15px] md:text-2xl">
            {{ t(previousRatingStored ? 'rating.stars.edit.title' : 'rating.stars.new.title') }}
          </h3>
        </figure>
        <section class="mb-6 flex flex-col items-center justify-center gap-8 md:mb-0">
          <p class="text-center text-sm tracking-[-0.15px]">
            {{ t('rating.stars.help') }}
          </p>
          <div class="mx-auto w-fit md:w-[60%]">
            <Rating v-model="value" data-test="rating-stars-selector" :cancel="false" />
          </div>
        </section>
      </section>
    </template>
    <template #actions>
      <div
        v-if="previousRatingStored"
        class="mx-auto flex flex-row md:my-6 md:w-fit md:justify-items-center md:gap-5"
      >
        <McdButton
          is-secondary
          class="h-16 w-1/2 whitespace-nowrap rounded-none md:h-fit md:rounded md:px-6 md:py-2"
          @on-click="emit('close', { closeModal: false })"
        >
          {{ t('rating.stars.edit.actions.notNow') }}
        </McdButton>

        <McdButton
          is-primary
          class="h-16 w-1/2 whitespace-nowrap rounded-none md:h-fit md:rounded md:px-6 md:py-2"
          @on-click="emit('set-stars', value ?? 1)"
        >
          {{ t('rating.stars.edit.actions.continue') }}
        </McdButton>
      </div>
    </template>
  </ModalCenter>
</template>

<style>
.p-rating .p-rating-item .p-rating-icon.p-icon {
  @apply h-12 w-12 text-gray-300;
}

.p-rating .p-rating-item.p-rating-item-active .p-rating-icon {
  @apply text-mcd-main;
}

.p-rating:not(.p-disabled):not(.p-readonly) .p-rating-item:hover .p-rating-icon {
  @apply text-mcd-main;
}
</style>
